import aiAssistantWaveformConfig from '../../consts/aiAssistant/aiAssistantWaveformConfig';

/**
 * Pure math for the waveform: one SVG path per strand for a moment and voice level. `timeS` is integrated by
 * the caller, so a pace change doesn't make the wave jump.
 */

const {
    WIDTH,
    CENTER_Y,
    MARGIN_X,
    SAMPLES,
    STRANDS,
    ROLL_HZ,
    ROLL_WOBBLE,
    ROLL_WOBBLE_HZ,
    SWAY,
    SWAY_WAVES,
    FAN,
    TWIST_HZ,
    HALF_THICKNESS,
    MIN_HALF_THICKNESS,
    TAPER,
    LEVEL_SWAY_GAIN,
    LEVEL_FAN_GAIN,
    LEVEL_THICKNESS_GAIN,
    LEVEL_PACE_GAIN,
} = aiAssistantWaveformConfig;

const TWO_PI = 2 * Math.PI;
const SPAN = WIDTH - 2 * MARGIN_X;
// `u` is the position along the wave: 0 = left end, 1 = right end
const SAMPLE_US = Array.from({length: SAMPLES}, (unused, index) => index / (SAMPLES - 1));

const getSine = (timeS, frequencyHz, phase = 0) => Math.sin(TWO_PI * frequencyHz * timeS + phase);

// 0 at both ends; a hump at roll 0, an S at π/2, a bowl at π. Normalised so every roll sways equally far
const getShape = (u, roll) =>
    (Math.sin(Math.PI * u) * Math.sin(Math.PI * u + roll)) / ((1 + Math.abs(Math.cos(roll))) / 2);

// 1 in the middle, 0 at the ends
const getTaper = (u) => Math.sin(Math.PI * u) ** TAPER;

const getLevelPace = (level) => 1 + LEVEL_PACE_GAIN * level;

const getPose = (timeS, level) => {
    const breath = SWAY_WAVES.reduce(
        (sum, {weight, frequencyHz, phase}) => sum + weight * getSine(timeS, frequencyHz, phase),
        1
    );

    return {
        roll: TWO_PI * ROLL_HZ * timeS + ROLL_WOBBLE * getSine(timeS, ROLL_WOBBLE_HZ),
        twist: TWO_PI * TWIST_HZ * timeS,
        sway: SWAY * breath * (1 + LEVEL_SWAY_GAIN * level),
        fan: FAN + LEVEL_FAN_GAIN * level,
        halfThickness: HALF_THICKNESS * (1 + LEVEL_THICKNESS_GAIN * level),
    };
};

const toPoint = (u, y) => `${(MARGIN_X + u * SPAN).toFixed(2)} ${y.toFixed(2)}`;

const toPath = (points) => `M${points.join('L')}`;

// cos(πu + twist) crosses zero at most once, so only one twist is visible at a time
const getRibbon = ({roll, twist, sway, halfThickness}) =>
    SAMPLE_US.map((u) => ({
        u,
        y: CENTER_Y + sway * getShape(u, roll),
        body: (halfThickness - MIN_HALF_THICKNESS) * getTaper(u),
        turn: Math.cos(Math.PI * u + twist),
    }));

// Offset by the body only, so all strands merge into one hairline at the ends. A closed outline, so the
// blur has no seam
const getStrandPath = ({lag, width}, ribbon, fan) => {
    const samples = ribbon.map(({u, y, body, turn}) => ({
        u,
        y: y + lag * fan * turn * body,
        half: MIN_HALF_THICKNESS + width * body,
    }));

    const upperEdge = samples.map(({u, y, half}) => toPoint(u, y - half));
    const lowerEdge = samples.map(({u, y, half}) => toPoint(u, y + half)).reverse();

    return `${toPath([...upperEdge, ...lowerEdge])}Z`;
};

// One closed path per strand, in STRANDS order
const getWaveformFrame = (timeS, level = 0) => {
    const pose = getPose(timeS, level);
    const ribbon = getRibbon(pose);

    return STRANDS.map((strand) => getStrandPath(strand, ribbon, pose.fan));
};

export default {getLevelPace, getWaveformFrame};
